import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import {
  capturePaymentRequest,
  fetchPaymentOperationsRequest,
  fetchPaymentRequest,
  reconcilePaymentsRequest,
  refundPaymentRequest,
} from '../services/paymentService.js'
import {
  canCapturePayment,
  canRefundPayment,
  canRetryPayment,
  normalizePaymentPayload,
  parsePaymentError,
} from '../utils/paymentUi.js'

export const usePaymentStore = defineStore('payment', () => {
  const payments = ref([])
  const currentPayment = ref(null)
  const loading = ref(false)
  const operationLoading = ref(false)
  const error = ref('')
  const degradedWarning = ref('')
  const partialWarning = ref('')
  const lastSuccessfulFetchAt = ref('')
  const operationReference = ref('')
  const locks = ref({})
  let requestToken = 0

  const paymentMap = computed(() => new Map(payments.value.map((payment) => [String(payment.id), payment])))

  const isPaymentLocked = (paymentId) => Boolean(locks.value[String(paymentId)])
  const setPaymentLocked = (paymentId, locked) => {
    locks.value = {
      ...locks.value,
      [String(paymentId)]: Boolean(locked),
    }
  }

  const normalizeList = (payload) => {
    const list = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.items)
        ? payload.items
        : Array.isArray(payload?.payments)
          ? payload.payments
          : Array.isArray(payload?.results)
            ? payload.results
            : []

    return list.map((entry, index) => normalizePaymentPayload(entry, index))
  }

  const fetchPaymentOperations = async (params = {}, options = {}) => {
    const token = ++requestToken
    loading.value = true
    error.value = ''
    degradedWarning.value = ''
    partialWarning.value = ''

    try {
      const data = await fetchPaymentOperationsRequest(params, options)
      if (token !== requestToken) return { success: false, cancelled: true }
      payments.value = normalizeList(data)
      if (!Array.isArray(data?.items) && !Array.isArray(data?.payments) && !Array.isArray(data?.results) && !Array.isArray(data)) {
        degradedWarning.value = 'Payment operations returned an unexpected payload shape. Showing defensive fallback view.'
      }
      if (data?.partial === true) {
        partialWarning.value = 'Payment operations are partially loaded. Reconciliation may still be in progress.'
      }
      lastSuccessfulFetchAt.value = new Date().toISOString()
      return { success: true, data: payments.value }
    } catch (err) {
      if (token !== requestToken) return { success: false, cancelled: true }
      error.value = parsePaymentError(err, 'Could not load payment operations.')
      return { success: false, error: error.value }
    } finally {
      if (token === requestToken) {
        loading.value = false
      }
    }
  }

  const fetchPayment = async (paymentId, options = {}) => {
    const token = ++requestToken
    loading.value = true
    error.value = ''
    degradedWarning.value = ''

    try {
      const data = await fetchPaymentRequest(paymentId, options)
      if (token !== requestToken) return { success: false, cancelled: true }
      currentPayment.value = normalizePaymentPayload(data)
      const existingIndex = payments.value.findIndex((item) => String(item.id) === String(currentPayment.value.id))
      if (existingIndex >= 0) {
        payments.value.splice(existingIndex, 1, currentPayment.value)
      } else {
        payments.value = [currentPayment.value, ...payments.value]
      }
      lastSuccessfulFetchAt.value = new Date().toISOString()
      return { success: true, data: currentPayment.value }
    } catch (err) {
      if (token !== requestToken) return { success: false, cancelled: true }
      error.value = parsePaymentError(err, 'Could not load payment details.')
      return { success: false, error: error.value }
    } finally {
      if (token === requestToken) {
        loading.value = false
      }
    }
  }

  const refreshPaymentAfterOperation = async (paymentId) => {
    const result = await fetchPayment(paymentId)
    if (!result.success) {
      degradedWarning.value = 'Payment action completed, but refresh confirmation is still pending.'
    }
  }

  const capturePayment = async (paymentId, payload = {}) => {
    if (isPaymentLocked(paymentId)) {
      return { success: false, code: 'PAYMENT_LOCKED', error: 'A payment action is already in progress.' }
    }

    const payment = paymentMap.value.get(String(paymentId)) ?? currentPayment.value
    if (payment && !canCapturePayment(payment)) {
      return { success: false, code: 'PAYMENT_CAPTURE_BLOCKED', error: 'This payment cannot be captured in its current state.' }
    }

    operationLoading.value = true
    error.value = ''
    operationReference.value = ''
    partialWarning.value = ''
    setPaymentLocked(paymentId, true)
    try {
      const data = await capturePaymentRequest(paymentId, payload)
      const normalized = normalizePaymentPayload(data)
      currentPayment.value = normalized
      operationReference.value = normalized.correlationRef || normalized.reference || ''
      if (normalized.partial || data?.partial === true) {
        partialWarning.value = 'Payment capture reported a partial result. Refresh before retrying.'
      }
      await refreshPaymentAfterOperation(paymentId)
      return { success: true, data: normalized }
    } catch (err) {
      error.value = parsePaymentError(err, 'Could not capture payment.')
      operationReference.value = String(err?.response?.data?.correlation_ref || err?.response?.headers?.['x-correlation-id'] || '')
      return { success: false, error: error.value }
    } finally {
      setPaymentLocked(paymentId, false)
      operationLoading.value = false
    }
  }

  const refundPayment = async (paymentId, payload = {}) => {
    if (isPaymentLocked(paymentId)) {
      return { success: false, code: 'PAYMENT_LOCKED', error: 'A payment action is already in progress.' }
    }

    const payment = paymentMap.value.get(String(paymentId)) ?? currentPayment.value
    if (payment && !canRefundPayment(payment)) {
      return { success: false, code: 'PAYMENT_REFUND_BLOCKED', error: 'This payment cannot be refunded in its current state.' }
    }

    operationLoading.value = true
    error.value = ''
    operationReference.value = ''
    partialWarning.value = ''
    setPaymentLocked(paymentId, true)
    try {
      const data = await refundPaymentRequest(paymentId, payload)
      const normalized = normalizePaymentPayload(data)
      currentPayment.value = normalized
      operationReference.value = normalized.correlationRef || normalized.reference || ''
      if (normalized.partial || data?.partial === true) {
        partialWarning.value = 'Payment refund reported a partial result. Review reconciliation before retrying.'
      }
      await refreshPaymentAfterOperation(paymentId)
      return { success: true, data: normalized }
    } catch (err) {
      error.value = parsePaymentError(err, 'Could not refund payment.')
      operationReference.value = String(err?.response?.data?.correlation_ref || err?.response?.headers?.['x-correlation-id'] || '')
      return { success: false, error: error.value }
    } finally {
      setPaymentLocked(paymentId, false)
      operationLoading.value = false
    }
  }

  const reconcilePayments = async (payload = {}) => {
    if (operationLoading.value) {
      return { success: false, code: 'PAYMENT_LOCKED', error: 'Another payment operation is still running.' }
    }

    operationLoading.value = true
    error.value = ''
    operationReference.value = ''
    degradedWarning.value = ''
    partialWarning.value = ''
    try {
      const data = await reconcilePaymentsRequest(payload)
      operationReference.value = String(data?.correlation_ref || data?.reference || '')
      const hasReconcileWarning = data?.partial === true || data?.degraded === true
      await fetchPaymentOperations(payload)
      if (hasReconcileWarning) {
        degradedWarning.value = 'Payment reconciliation finished with warnings. Review operational drift before retrying actions.'
      }
      return { success: true, data }
    } catch (err) {
      error.value = parsePaymentError(err, 'Could not reconcile payment operations.')
      operationReference.value = String(err?.response?.data?.correlation_ref || err?.response?.headers?.['x-correlation-id'] || '')
      return { success: false, error: error.value }
    } finally {
      operationLoading.value = false
    }
  }

  const retryPayment = async (paymentId, payload = {}) => {
    if (isPaymentLocked(paymentId)) {
      return { success: false, code: 'PAYMENT_LOCKED', error: 'A payment action is already in progress.' }
    }

    const payment = paymentMap.value.get(String(paymentId)) ?? currentPayment.value
    if (payment && !canRetryPayment(payment)) {
      return { success: false, code: 'PAYMENT_RETRY_BLOCKED', error: 'This payment cannot be retried in its current state.' }
    }

    operationLoading.value = true
    error.value = ''
    operationReference.value = ''
    partialWarning.value = ''
    setPaymentLocked(paymentId, true)
    try {
      const data = await capturePaymentRequest(paymentId, {
        ...payload,
        retry: true,
      })
      const normalized = normalizePaymentPayload(data)
      currentPayment.value = normalized
      operationReference.value = normalized.correlationRef || normalized.reference || ''
      if (normalized.partial || data?.partial === true) {
        partialWarning.value = 'Payment retry reported a partial result. Refresh before retrying again.'
      }
      await refreshPaymentAfterOperation(paymentId)
      return { success: true, data: normalized }
    } catch (err) {
      error.value = parsePaymentError(err, 'Could not retry payment.')
      operationReference.value = String(err?.response?.data?.correlation_ref || err?.response?.headers?.['x-correlation-id'] || '')
      return { success: false, error: error.value }
    } finally {
      setPaymentLocked(paymentId, false)
      operationLoading.value = false
    }
  }

  return {
    payments,
    currentPayment,
    loading,
    operationLoading,
    error,
    degradedWarning,
    partialWarning,
    lastSuccessfulFetchAt,
    operationReference,
    locks,
    fetchPaymentOperations,
    fetchPayment,
    capturePayment,
    retryPayment,
    refundPayment,
    reconcilePayments,
    isPaymentLocked,
    canRetryPayment,
  }
})





